import CryptoJS from "crypto-js";
import {v4 as uuidv4} from 'uuid';

/* ============================
   常量定义
   ============================ */
export const EVENTS = {
    CLICK: 'click',
    INPUT: 'input',
    PAGE_IN: 'page-in',
    PAGE_LEAVE: 'page-leave',
    ERROR: 'error',
    CUSTOM: 'custom',
};

export const ERROR_TYPES = {
    JS_ERROR: 'js-error',
    API_ERROR: 'api-error',
    PROMISE_ERROR: 'promise-error',
    RESOURCE_ERROR: 'resource-error',
    FETCH_ERROR: 'fetch-error',
    CONSOLE_ERROR: 'console-error'
};

/* ============================
   QueueManager - 队列管理
   ============================ */
class QueueManager {
    constructor(tracker, options) {
        this.tracker = tracker;
        this.queue = [];
        this.maxQueueLength = options.maxQueueLength || 20;
        this.flushInterval = options.flushInterval || 10000;
        this.maxCache = options.maxCache || 500;
        this.concurrentFlushLimit = options.concurrentFlushLimit || 3;
        this.activeFlushCount = 0;
        this.sender = options.sender;
        this.endpoint = options.endpoint;

        this.flushTimer = null;
        this.startFlushTimer();
    }

    enqueue(payload) {
        this.queue.push(payload);
        this.tracker.log('info', 'Track queued', payload);

        if (this.queue.length >= this.maxQueueLength) this.flush();
    }

    async flush() {
        if (this.queue.length === 0 || this.activeFlushCount >= this.concurrentFlushLimit) return;

        const batch = this.queue.splice(0, this.maxQueueLength);
        this.activeFlushCount++;
        this.tracker.log('info', 'Flushing batch', batch.length);

        const timestamp = Date.now().toString();
        const sign = CryptoJS.MD5(
            `${timestamp}`
        ).toString();

        const payload = JSON.stringify(batch);

        try {
            await this.sender(this.endpoint, payload, sign, timestamp);
            this.tracker.log('info', 'Flushed (sender):', batch.length);
        } catch (e) {
            this.tracker.log('warn', 'Flush failed, caching events', e);
            this.cachePendingEvents(batch);
        } finally {
            this.activeFlushCount--;
            if (this.queue.length > 0) this.flush();
        }
    }

    startFlushTimer() {
        if (this.flushTimer) return;
        this.flushTimer = setInterval(() => {
            if (this.queue.length > 0) {
                this.tracker.log('info', 'Flush timer triggered');
                this.flush();
            }
        }, this.flushInterval);
    }

    stopFlushTimer() {
        if (this.flushTimer) {
            clearInterval(this.flushTimer);
            this.flushTimer = null;
        }
    }

    cachePendingEvents(events) {
        try {
            const key = 'tracker_pending';
            const existing = JSON.parse(localStorage.getItem(key) || '[]');
            const merged = existing.concat(events);
            const sliced = merged.slice(-this.maxCache);
            localStorage.setItem(key, JSON.stringify(sliced));
            this.tracker.log('info', 'Cached events to localStorage, total=', sliced.length);
        } catch (e) {
            this.tracker.log('error', 'cachePendingEvents failed', e);
        }
    }

    restorePendingEvents() {
        try {
            const pending = localStorage.getItem('tracker_pending');
            if (!pending) return [];
            const data = JSON.parse(pending);
            localStorage.removeItem('tracker_pending');
            this.queue.push(...data);
            this.tracker.log('info', 'Restored pending events:', data.length);
            return data;
        } catch (e) {
            this.tracker.log('warn', 'restorePendingEvents failed', e);
            return [];
        }
    }
}

/* ============================
   Sender - 数据发送器
   ============================ */
class Sender {
    static async defaultSender(endpoint, data, sign, timestamp) {
        const res = await fetch(endpoint, {
            method: 'POST',
            headers: {'Content-Type': 'application/json', sign, timestamp},
            body: data,
            keepalive: true
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const json = await res.json().catch(() => ({}));
        if (json.errorCode !== 0) throw new Error(`API ${json.errorCode}`);
        return res;
    }
}

/* ============================
   ErrorTracker - 错误监控
   ============================ */
class ErrorTracker {
    constructor(tracker) {
        this.tracker = tracker;
        this.listeners = {};
        this.originalConsoleError = null;
        this.originalFetch = null;
    }

    init({enableJsError, enablePromiseError, enableResourceError, enableFetchError, enableConsoleError}) {
        if (enableJsError) this.setupJsError();
        if (enablePromiseError) this.setupPromiseError();
        if (enableResourceError) this.setupResourceError();
        if (enableFetchError) this.setupFetchError();
        if (enableConsoleError) this.setupConsoleError();
        this.tracker.log('info', 'Error tracking initialized');
    }

    setupJsError() {
        const handler = (event) => {
            const {message, filename, lineno, colno, error} = event;
            this.tracker.trackError(ERROR_TYPES.JS_ERROR, {
                message: message || (error && error.message),
                stack: error?.stack,
                filename,
                lineno,
                colno
            });
        };
        window.addEventListener('error', handler);
        this.listeners.jsError = handler;
    }

    setupPromiseError() {
        const handler = (event) => {
            const error = event.reason;
            this.tracker.trackError(ERROR_TYPES.PROMISE_ERROR, {
                message: error?.message || 'Promise rejected',
                stack: error?.stack,
            });
        };
        window.addEventListener('unhandledrejection', handler);
        this.listeners.promiseError = handler;
    }

    setupResourceError() {
        const handler = (event) => {
            const target = event.target;
            if (target === window) return;
            const url = target.src || target.href;
            const types = ['IMG', 'SCRIPT', 'LINK', 'VIDEO', 'AUDIO'];
            if (!types.includes(target.tagName)) return;
            this.tracker.trackError(ERROR_TYPES.RESOURCE_ERROR, {
                message: `Resource load failed: ${url}`,
                tagName: target.tagName,
                resourceUrl: url
            });
        };
        window.addEventListener('error', handler, true);
        this.listeners.resourceError = handler;
    }

    setupFetchError() {
        const originalFetch = window.fetch;
        const tracker = this.tracker;
        window.fetch = async function (...args) {
            const start = Date.now();
            const [requestInfo, requestInit] = args;
            const url = typeof requestInfo === 'string' ? requestInfo : requestInfo.url;
            const method = (requestInit?.method || 'GET');
            if (url.includes(tracker.endpoint)) return originalFetch(...args);

            try {
                const res = await originalFetch(...args);
                if (!res.ok) {
                    tracker.trackError(ERROR_TYPES.FETCH_ERROR, {
                        message: `Fetch Error: ${res.status} ${res.statusText}`,
                        method,
                        url,
                        status: res.status,
                        duration: Date.now() - start
                    });
                }
                return res;
            } catch (err) {
                tracker.trackError(ERROR_TYPES.FETCH_ERROR, {
                    message: `Fetch Network Error: ${err.message}`,
                    method,
                    url,
                    duration: Date.now() - start,
                    stack: err.stack
                });
                throw err;
            }
        };
        this.originalFetch = originalFetch;
    }

    setupConsoleError() {
        const tracker = this.tracker;
        const original = console.error;
        console.error = function (...args) {
            original.apply(console, args);
            tracker.trackError(ERROR_TYPES.CONSOLE_ERROR, {
                message: args.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a)).join(' '),
                consoleArgs: args
            });
        };
        this.originalConsoleError = original;
    }
}

/* ============================
   AutoTracker - DOM & Router
   ============================ */
class AutoTracker {
    constructor(tracker) {
        this.tracker = tracker;
    }

    bindDOM() {
        document.addEventListener('click', (e) => {
            try {
                const el = e.target.closest('[data-track-widget-id],[data-trackwidgetid],[data-track-widget]');
                if (!el) return;
                const data = this.extractData(el);
                this.tracker.track(EVENTS.CLICK, data);
            } catch {
            }
        }, true);

        document.addEventListener('input', (e) => {
            try {
                const el = e.target;
                const label = el.dataset.trackInput || el.getAttribute('data-track-input');
                const safe = (el.dataset.trackSafe || el.getAttribute('data-track-safe')) === 'true';
                if (!label || !safe) return;
                const value = (el.value || '').toString().slice(0, 100).replace(/\d{3,}/g, '***');
                this.tracker.track(EVENTS.INPUT, {label, tag: el.tagName, value});
            } catch {
            }
        }, true);
    }

    bindRouter(router) {
        if (!router || typeof router.afterEach !== 'function') return;
        let firstLoad = true;
        router.afterEach((to, from) => {
            this.tracker.track(EVENTS.PAGE_IN, {from: firstLoad ? null : from.name, to: to.name});
            firstLoad = false;
        });
    }

    bindLifecycle() {
        window.addEventListener('beforeunload', () => {
            this.tracker.track(EVENTS.PAGE_LEAVE);
            this.tracker.manualFlush();
            this.tracker.stop();
        });
    }

    extractData(el) {
        const obj = {};
        for (const key in el.dataset) {
            if (key.startsWith('track')) {
                const normalized = key.slice(5);
                const finalKey = normalized.charAt(0).toLowerCase() + normalized.slice(1);
                obj[finalKey] = el.dataset[key];
            }
        }
        const attrs = el.attributes || [];
        for (let i = 0; i < attrs.length; i++) {
            const n = attrs[i].name;
            if (!n.startsWith('data-track-')) continue;
            const key = n.slice(11).split('-').map((w, idx) => idx ? w[0].toUpperCase() + w.slice(1) : w).join('');
            if (!obj[key]) obj[key] = attrs[i].value;
        }
        return obj;
    }
}

/* ============================
   Tracker SDK 主类
   ============================ */
export class Tracker {
    static instance = null;

    constructor(options = {}) {
        if (Tracker.instance) return Tracker.instance;
        Tracker.instance = this;

        // ---------- 配置 ----------
        this.endpoint = options.endpoint || `${process.env.VUE_APP_DOMAIN}/api/event_tracking/save`;
        this.project = options.project || 'default';
        this.debug = options.debug || false;
        this.sampleRate = options.sampleRate || 1.0;
        this.enableTracking = options.enableTracking !== false;

        this.userId = this.getOrCreateUserId();
        this.sessionId = uuidv4();

        // ---------- 子模块 ----------
        this.queueManager = new QueueManager(this, {
            maxQueueLength: options.maxQueueLength,
            flushInterval: options.flushInterval,
            maxCache: options.maxCache,
            concurrentFlushLimit: options.concurrentFlushLimit,
            sender: options.sender || Sender.defaultSender,
            endpoint: this.endpoint
        });

        this.errorTracker = new ErrorTracker(this);
        this.autoTracker = new AutoTracker(this);

        this.errorTracker.init({
            enableJsError: options.enableJsError ?? true,
            enablePromiseError: options.enablePromiseError ?? true,
            enableResourceError: options.enableResourceError ?? true,
            enableFetchError: options.enableFetchError ?? true,
            enableConsoleError: options.enableConsoleError ?? false
        });

        this.autoTracker.bindDOM();
        this.autoTracker.bindRouter(options.router);
        this.autoTracker.bindLifecycle();

        this.queueManager.restorePendingEvents();
        this.log('info', 'Tracker initialized');
    }

    log(levelOrMessage, ...args) {
        if (!this.debug) return;
        const levels = ['info', 'warn', 'error'];
        const level = levels.includes(levelOrMessage) ? levelOrMessage : 'info';
        const message = levels.includes(levelOrMessage) ? args : [levelOrMessage, ...args];
        if (console[level]) console[level]('%c[Tracker]', 'color:#007ACC;', ...message);
        else console.log('%c[Tracker]', 'color:#007ACC;', ...message);
    }

    shouldSample() {
        return Math.random() <= this.sampleRate;
    }

    getOrCreateUserId() {
        try {
            const key = 'tracker_user_id';
            let id = localStorage.getItem(key);
            if (!id) {
                id = uuidv4();
                localStorage.setItem(key, id);
            } else id = id.replace(/^"(.*)"$/, '$1');
            return id;
        } catch (e) {
            this.log('warn', 'localStorage not available', e);
            this.trackError(ERROR_TYPES.JS_ERROR, {
                message: 'localStorage not available',
                stack: e.stack
            });
            return null;
        }
    }

    buildPayload(eventType, data = {}) {
        return {
            id: uuidv4(),
            type: eventType,
            project: this.project,
            userId: this.userId,
            sessionId: this.sessionId,
            eventTime: Date.now(),
            pageId: 'home',
            pageUrl: window.location.href,
            referrer: document.referrer || null,
            env: {
                userAgent: navigator.userAgent,
                language: navigator.language,
                screen: `${window.screen.width}x${window.screen.height}`,
                platform: navigator.platform
            },
            params: {...data},
            widgetId: data.widgetId,
            widgetLabel: data.widgetLabel,
            eventValue: data.eventValue
        };
    }

    validateEvent(payload) {
        return !!(payload && payload.type && payload.eventTime);
    }

    track(eventType, data = {}) {
        try {
            if (!this.enableTracking) return;
            if (!this.shouldSample()) return;
            const payload = this.buildPayload(eventType, data);
            if (!this.validateEvent(payload)) return;
            this.queueManager.enqueue(payload);
        } catch (e) {
            this.log('error', 'track error', e);
        }
    }

    trackError(errorType, data = {}) {
        if (!this.enableTracking) return;
        const payload = this.buildPayload(EVENTS.ERROR, {errorType, ...data});
        if (!this.validateEvent(payload)) return;
        this.queueManager.enqueue(payload);
        this.queueManager.flush();
    }

    manualFlush() {
        return this.queueManager.flush();
    }

    stop() {
        this.queueManager.stopFlushTimer();
        if (this.errorTracker.originalConsoleError) console.error = this.errorTracker.originalConsoleError;
        if (this.errorTracker.originalFetch) window.fetch = this.errorTracker.originalFetch;
    }
}
